import { FormattedMessage } from "react-intl";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import SystemStatusService from "../../../services/SystemStatusService";
import ISystemStatusMessageUI from "../../../models/ISystemStatusMessageUI";
import "./SystemMessagesWidget.scss";

type Props = {
    /** Legacy messages supplied by the dashboard load (currently always empty). */
    systemMessages?: string[];
    removeWidget?: () => void;
    inEditor?: boolean;
};

export const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const SystemMessagesWidget = (props: Props) => {
    const [isLoading, setIsLoading] = useState<boolean>(!props.inEditor);
    const [statusMessage, setStatusMessage] = useState<ISystemStatusMessageUI | null>(null);

    useEffect(() => {
        if (props.inEditor) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            const result = await SystemStatusService.fetchSystemStatus();

            if (cancelled) {
                return;
            }

            setStatusMessage(result);
            setIsLoading(false);
        };

        load();

        // Dashboards stay open all day; keep the message current
        const intervalId = window.setInterval(load, REFRESH_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [props.inEditor]);

    const getMessageText = (status: ISystemStatusMessageUI): React.ReactNode => {
        if (status.message) {
            return status.message;
        }

        switch (status.kind) {
            case "maintenance_in_progress":
                return (
                    <FormattedMessage
                        id="dashboard.system_messages.maintenance_in_progress"
                        defaultMessage="Orderwerks is currently undergoing scheduled maintenance"
                        description="Default text when maintenance is underway and the status file has no message"
                    />
                );
            default:
                return null;
        }
    };

    const getSubLine = (status: ISystemStatusMessageUI): React.ReactNode => {
        switch (status.kind) {
            case "maintenance_in_progress":
                return status.endTime ? (
                    <div className={"SystemMessagesWidget__sub"}>
                        <FormattedMessage
                            id="dashboard.system_messages.expected_back_by"
                            defaultMessage="Expected back by"
                            description="Label preceding the expected end time of maintenance"
                        />
                        : {status.endTime}
                    </div>
                ) : null;
            case "maintenance_scheduled":
            case "announcement":
            default:
                return status.timeframe ? (
                    <div className={"SystemMessagesWidget__sub"}>
                        <FormattedMessage id="scheduled" defaultMessage="Scheduled" description="Scheduled" />: {status.timeframe}
                    </div>
                ) : null;
        }
    };

    const legacyMessages = props.systemMessages || [];
    const hasContent = statusMessage !== null || legacyMessages.length > 0;

    let body: React.ReactNode;

    if (isLoading) {
        body = (
            <div className="d-flex text-center justify-content-center align-items-center flex-grow-1 h-100 w-100">
                <OwLabel title={"loading"} leftIconClass={"fad fa-spinner fa-spin"} />
            </div>
        );
    } else if (hasContent) {
        body = (
            <div className={"w-100 d-flex flex-column flex-grow-1 justify-content-start"}>
                {statusMessage ? (
                    <div
                        className={"SystemMessagesWidget__message"}
                        role={"status"}
                        style={{ "--system-message-accent": statusMessage.accentColor } as React.CSSProperties}>
                        <i className={statusMessage.icon + " SystemMessagesWidget__icon"} aria-hidden="true" />
                        <div className={"SystemMessagesWidget__content"}>
                            <div className={"SystemMessagesWidget__text"}>{getMessageText(statusMessage)}</div>
                            {getSubLine(statusMessage)}
                        </div>
                    </div>
                ) : null}
                {legacyMessages.map((msg: string, index: number) => {
                    return (
                        <div key={index} className={"col-12"}>
                            {msg}
                        </div>
                    );
                })}
            </div>
        );
    } else {
        body = (
            <div className="d-flex text-center justify-content-center align-items-center flex-grow h-100 w-100">
                <FormattedMessage id="dashboard.no_system_messages" defaultMessage="No System Messages" description="No System Messages" />
            </div>
        );
    }

    return (
        <WidgetContainer className={"SystemMessagesWidget widget h-100 d-flex flex-column justify-content-start"}>
            {props.inEditor ? (
                <div>
                    <div className={"w-100"}>
                        <div>
                            <OwIconButton
                                iconClass={"fas fa-trash-alt"}
                                iconColorClass={"text-danger"}
                                fn={() => {
                                    props.removeWidget!();
                                }}
                            />
                        </div>
                        <div>
                            <OwLabel title={"system_messages"} />
                        </div>
                    </div>
                </div>
            ) : (
                body
            )}
        </WidgetContainer>
    );
};

const WidgetContainer = styled.div`
    padding: 0.5rem;
    justify-content: start !important;
    align-items: start !important;
    height: 100%;
    /* The grid gives the widget a fixed height; a long message should scroll
       inside the widget rather than spill onto the widget below. */
    overflow-x: hidden;
    overflow-y: auto;

    .position-relative {
        width: 100%;
    }
`;
